import { apiInternals, send } from './client';

/**
 * The bell panel's reader over the `notifications` table (M-16, Task 3,
 * spec §3) — `GET /notifications`, `GET /notifications/unread-count`,
 * `POST /notifications/{uuid}/read` and `POST /notifications/read-all`.
 * Every shape here is verified field-for-field against
 * `App\Http\Resources\Notifications\NotificationResource` and
 * `App\Http\Controllers\Api\V1\Notifications\*` in the API repository —
 * `replacements.ts`'s own docblock states the same discipline, and this
 * one repeats it.
 *
 * ## `list` is built on `request`, never `send`
 *
 * `GET /notifications` is cursor-paginated — `{ data, meta: { next_cursor,
 * per_page, total, unread_count } }`. `send()` resolves to `request()`'s
 * `.data` alone and throws `meta` away, so a caller built on it silently
 * loses BOTH `next_cursor` and `unread_count`.
 *
 * `meta.unread_count` IS THE CALLER'S ACCOUNT-WIDE UNREAD TOTAL, not this
 * page's and not narrowed by `unread`/`type` — it exists so opening the
 * panel refreshes the bell badge without a second request. Never derive an
 * unread count from `data` locally; a page of 20 read rows would report 0
 * when the account's real total is nonzero.
 *
 * ## `unread` serialises as `1`/`0`, never `true`/`false`
 *
 * Laravel's `boolean` rule accepts `true`/`false`/`1`/`0`/`'1'`/`'0'` — the
 * STRING `'true'` (`String(true)`) is rejected, a `422` for the whole
 * request. `queryString()` below carries the identical fix the sibling
 * modules document; that defect was live in four modules at once before
 * M-12 caught it.
 *
 * ## `unreadCount()` and `markAllRead()` return the NUMBER, not the envelope
 *
 * Every caller wants the integer, and unwrapping `{ data: { unread_count }
 * }` / `{ data: { marked } }` in four call sites is three more places to
 * get it wrong.
 *
 * ## There is no `case_id` and no `can` block, and both are deliberate
 *
 * `NotificationResource` publishes neither (spec §2) — see that resource's
 * own docblock in the API repository. This module does not invent either
 * field.
 */

/** The six types the five existing writers emit (`NotificationIndexRequest::TYPES`). An unfamiliar string is not an error here — see `notificationTarget.ts`. */
export type NotificationType =
  | 'escalation.assigned'
  | 'justification.raised'
  | 'justification.answered'
  | 'justification.decided'
  | 'knowledge.published'
  | 'replacement.requested';

/**
 * One notification, exactly as `NotificationResource` renders it. `data`
 * is a DECODED OBJECT, never a JSON string — its keys differ per `type`
 * and that is deliberate (spec N-5).
 *
 * ## `body` and `data` are nullable, matching the contract, not today's writers
 *
 * The migration (`2026_08_01_000600_create_messaging_tables.php`) declares
 * both columns nullable, and the OpenAPI schema publishes
 * `['type' => ['string', 'null']]`/`['object', 'null']` for them — the five
 * existing writers all feed `body` from genuinely nullable source columns
 * (`escalations.reason`, `replacements.reason`,
 * `justifications.justification`/`decision_note`) and always
 * `json_encode()` an array for `data`, so BOTH happen to be non-null in
 * every row this client has ever seen. That is a fact about today's five
 * writers, not a guarantee the column makes — narrowing the type to match
 * current callers rather than the contract is exactly how the NEXT writer,
 * or a row this client reads before its own write lands, becomes a
 * `.trim()`/`.length` crash no fixture in either repo would catch.
 */
export interface NotificationRow {
  uuid: string;
  type: NotificationType | string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  read_at: string | null;
  created_at: string | null;
}

/**
 * Every filter `GET /notifications` accepts (`NotificationIndexRequest::rules()`).
 * All optional. `unread` is a narrowing flag, not a tristate — `unread:
 * false` and omitting it both mean "do not narrow" (that request's own
 * docblock).
 */
export interface NotificationQuery {
  unread?: boolean;
  type?: NotificationType | string;
  cursor?: string;
  per_page?: number;
}

/** One page of the panel — cursor-paginated. */
export interface NotificationPage {
  data: readonly NotificationRow[];
  meta: {
    next_cursor: string | null;
    per_page: number;
    /** How many notifications match the selected filters, not how many are on this page. */
    total: number;
    /** The caller's ACCOUNT-WIDE unread total — not this page's, not narrowed by `unread`/`type`. */
    unread_count: number;
  };
}

/**
 * `?key=value&...`, or `''` when every value is absent. `undefined` is
 * dropped rather than becoming the literal string `'undefined'` in the URL —
 * the identical helper `replacements.ts` and its siblings each redeclare
 * locally for the same reason (none is exported).
 */
function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | boolean | undefined][]) {
    if (value === undefined) continue;

    // A BOOLEAN GOES OVER THE WIRE AS `1`/`0`, NEVER `true`/`false` — see
    // this module's own docblock for why `unread=true` would 422.
    search.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

const { request } = apiInternals;

export const notificationsApi = {
  /**
   * DELIBERATELY NOT `send<NotificationPage>()` — see this module's own
   * docblock for why that would compile and still lose `meta.next_cursor`
   * AND `meta.unread_count`.
   */
  list: async (query: NotificationQuery = {}): Promise<NotificationPage> => {
    const { data, meta } = await request<readonly NotificationRow[]>(`/notifications${queryString(query)}`);
    return { data, meta: meta as NotificationPage['meta'] };
  },

  /** The badge on its own — what the panel polls between opens. Returns the NUMBER, not `{ data: { unread_count } }`. */
  unreadCount: async (): Promise<number> => {
    const { unread_count: unreadCount } = await send<{ unread_count: number }>('/notifications/unread-count');
    return unreadCount;
  },

  /** `200` even on a row already read, republishing the ORIGINAL `read_at` — never a `409`. */
  markRead: (uuid: string) => send<NotificationRow>(`/notifications/${uuid}/read`, { method: 'POST', background: true }),

  /** Returns how many rows this call actually changed, not how many the caller has — `0` on a second click. */
  markAllRead: async (): Promise<number> => {
    const { marked } = await send<{ marked: number }>('/notifications/read-all', { method: 'POST', background: true });
    return marked;
  },
};
